import { takeLatest, fork, put, select } from "redux-saga/effects";
import ApiClient from "../../../../api/ApiClient";
import {
  AUTH_LOGIN_TYPE,
  AUTH_LOGIN_TYPE_BODY_KEY
} from "../../../../lib/auth/authLoginTypes";
import {
  OTP_BE_FIELD_TO_FORM_FIELD,
  OTP_FORM_FIELD
} from "../../../../lib/auth/authOtpConstants";
import { resolveApiFormErrorMessage } from "../../../../lib/api/resolveApiFormErrorMessage";
import authFormFieldsToRequestData from "../../../../lib/authFormFieldsToRequestData";
import appendLangToAuthRequestData from "../../../../lib/auth/appendLangToAuthRequestData";
import { getIsWeb } from "../../../../lib/platformHelper";
import { getLocale } from "../../translations/selector";
import config from "../../../../config";
import { actions as alertsActions } from "../../alerts";
import { afterLogin } from "../login/saga";
import {
  otpRequestRoutine,
  registerRoutine,
  verifyOtpHashRoutine,
  verifyOtpRoutine
} from "./index";

function parseErrorResponse(error) {
  if (!error?.request?.response) {
    return {};
  }

  try {
    return JSON.parse(error.request.response);
  } catch (parseError) {
    return {};
  }
}

function mapOtpValidationFields(validation) {
  if (!Array.isArray(validation)) {
    return [];
  }

  return validation.map(item => ({
    ...item,
    field:
      OTP_BE_FIELD_TO_FORM_FIELD[item.field] ||
      item.field ||
      OTP_FORM_FIELD.CODE
  }));
}

function* onRegister({
  payload: { fields, recaptcha, hideModal, onSuccess, redirectTo }
}) {
  const locale = yield select(getLocale);
  const requestData = {
    ...appendLangToAuthRequestData(authFormFieldsToRequestData(fields), locale),
    recaptcha
  };

  if (typeof redirectTo === "string" && redirectTo.length > 0) {
    requestData.redirect_to = redirectTo;
  }

  try {
    const { data } = yield ApiClient.post({
      urlPath: "app.auth.otpRegister",
      data: requestData
    });

    if (hideModal) {
      hideModal();
    }

    if (typeof onSuccess === "function") {
      onSuccess(data);
    }

    yield put(registerRoutine.success(data));
  } catch (error) {
    if (error.request) {
      const response = parseErrorResponse(error);
      const validation = Array.isArray(response.validation)
        ? response.validation
        : [];

      yield put(registerRoutine.failure(validation));

      yield put(
        alertsActions.addErrorAlert({
          title: resolveApiFormErrorMessage(response)
        })
      );
    } else {
      yield put(registerRoutine.failure([]));
    }
  }
}

function* onVerifyOtp({
  payload: { user, code, recaptcha, fromRegister = false }
}) {
  try {
    const { data } = yield ApiClient.post({
      urlPath: "app.auth.login",
      data: {
        [AUTH_LOGIN_TYPE_BODY_KEY]: AUTH_LOGIN_TYPE.OTP_CODE,
        user,
        code,
        recaptcha
      }
    });

    const { token } = data;

    if (fromRegister && getIsWeb()) {
      localStorage.setItem(config.introductionModalStorageKey, "true");
    }

    yield afterLogin(token, fromRegister, false, fromRegister ? "form" : null);
    yield put(verifyOtpRoutine.success(data));
  } catch (error) {
    if (error.request) {
      const response = parseErrorResponse(error);
      const validation = mapOtpValidationFields(response.validation);

      yield put(verifyOtpRoutine.failure(validation));

      yield put(
        alertsActions.addErrorAlert({
          title: resolveApiFormErrorMessage(response)
        })
      );
    } else {
      yield put(verifyOtpRoutine.failure([]));
    }
  }
}

function* onVerifyOtpHash({
  payload: { hash, recaptcha, fromRegister = false }
}) {
  try {
    const { data } = yield ApiClient.post({
      urlPath: "app.auth.login",
      data: {
        [AUTH_LOGIN_TYPE_BODY_KEY]: AUTH_LOGIN_TYPE.OTP_HASH,
        hash,
        recaptcha
      }
    });

    const { token } = data;

    if (fromRegister && getIsWeb()) {
      localStorage.setItem(config.introductionModalStorageKey, "true");
    }

    yield afterLogin(token, fromRegister, false, fromRegister ? "form" : null);
    yield put(verifyOtpHashRoutine.success(data));
  } catch (error) {
    if (error.request) {
      const response = parseErrorResponse(error);
      const validation = mapOtpValidationFields(response.validation);

      yield put(verifyOtpHashRoutine.failure(validation));

      yield put(
        alertsActions.addErrorAlert({
          title: resolveApiFormErrorMessage(response)
        })
      );
    } else {
      yield put(verifyOtpHashRoutine.failure([]));
    }
  }
}

function* onOtpRequest({
  payload: { user, recaptcha, fromRegister = false, onSuccess, redirectTo }
}) {
  const requestData = {
    user,
    recaptcha,
    from_register: fromRegister
  };

  if (typeof redirectTo === "string" && redirectTo.length > 0) {
    requestData.redirect_to = redirectTo;
  }

  try {
    const { data } = yield ApiClient.post({
      urlPath: "app.auth.otpRequest",
      data: requestData
    });

    yield put(otpRequestRoutine.success(data));

    if (typeof onSuccess === "function") {
      onSuccess(data);
    }
  } catch (error) {
    if (error.request) {
      const response = parseErrorResponse(error);
      const validation = Array.isArray(response.validation)
        ? response.validation
        : [];

      yield put(otpRequestRoutine.failure(validation));

      yield put(
        alertsActions.addErrorAlert({
          title: resolveApiFormErrorMessage(response)
        })
      );
    } else {
      yield put(otpRequestRoutine.failure([]));
    }
  }
}

function* watchRegister() {
  yield takeLatest(registerRoutine.TRIGGER, onRegister);
}

function* watchVerifyOtp() {
  yield takeLatest(verifyOtpRoutine.TRIGGER, onVerifyOtp);
}

function* watchVerifyOtpHash() {
  yield takeLatest(verifyOtpHashRoutine.TRIGGER, onVerifyOtpHash);
}

function* watchOtpRequest() {
  yield takeLatest(otpRequestRoutine.TRIGGER, onOtpRequest);
}

export default [
  fork(watchRegister),
  fork(watchVerifyOtp),
  fork(watchVerifyOtpHash),
  fork(watchOtpRequest)
];

export { onOtpRequest, onRegister, onVerifyOtp, onVerifyOtpHash };
